import { type AnalyticsAttribute, type SrpEventStatus } from './types';
import { type TripSearchForm } from '@search/trip/TripSearchForm';
import { type TrainSearchForm } from '@search/trip/train/TrainSearchForm';
import {
    SearchLogAPIApiApiSearchLogAddlogPost,
    type SearchLogAPIApiApiSearchLogAddlogPostRequest,
} from '@clients/UserLogApi';
import { ReserveType } from '@utils/itinerary';
import { type UserInfo } from '@store/auth';
import { TripType } from '@utils/trip';

type Attrs = Record<string, any>;
type TrackerType = 'posthog' | 'gtm' | 'webengage';
export class AnalyticsStore {
    searchInputSearchCount = {
        paxPicker: 0,
        citySelect: 0,
        datePicker: 0,
    };

    resetSearchInputSearchCount() {
        this.searchInputSearchCount = {
            paxPicker: 0,
            citySelect: 0,
            datePicker: 0,
        };
    }

    trackWebengage(name: string, value?: Attrs) {
        const attrs = {
            ...(value || {}),
            ...this.globalAttributes,
        };
        if (useDevice().isWebApp.value) {
            window.WebAppWebEngageChannel?.postMessage(
                JSON.stringify({
                    type: 'event',
                    name,
                    attrs,
                })
            );
        } else {
            window.webengage?.track(name, attrs);
        }
    }

    setWebengageUserInfo(userInfo: Partial<UserInfo>) {
        if (useDevice().isWebApp.value) {
            window.WebAppWebEngageChannel?.postMessage(
                JSON.stringify({
                    type: 'user',
                    userInfo,
                })
            );
        } else {
            if (userInfo.title) {
                window.webengage?.user.setAttribute(
                    'first_name',
                    userInfo.title
                );
            }
            if (userInfo.lastName) {
                window.webengage?.user.setAttribute(
                    'last_name',
                    userInfo.lastName
                );
            }
            if (userInfo.birthDate) {
                window.webengage?.user.setAttribute(
                    'birth_date',
                    userInfo.birthDate
                );
            }
            if (userInfo.email) {
                window.webengage?.user.setAttribute('email', userInfo.email);
            }
            if (userInfo.mobile) {
                window.webengage?.user.setAttribute('phone', userInfo.mobile);
            }
        }
    }

    trackMetrix(name: string, attrs?: Attrs) {
        const payload = {
            ...(attrs || {}),
            ...this.globalAttributes,
        };
        window.WebAppMetrixEventChannel?.postMessage(
            JSON.stringify({
                name,
                params: payload,
            })
        );
    }

    trackPosthog(name: string, attrs?: Attrs) {
        const { trackPostHog } = usePostHog();
        const payload = {
            ...(attrs || {}),
            ...this.globalAttributes,
        };
        if (window.WebAppPosthogChannel) {
            window.WebAppPosthogChannel.postMessage(
                JSON.stringify({
                    name,
                    params: payload,
                })
            );
        } else {
            trackPostHog(name, payload);
        }
    }

    trackGtm(event: string, attrs?: Attrs) {
        const value = {
            ...(attrs || {}),
            ...this.globalAttributes,
        };
        useGtm()?.trackEvent({
            event,
            value,
        });
    }

    private get globalAttributes() {
        const authStore = useAuthStore();
        return {
            vpn: authStore.vpn,
            isLoggedIn: authStore.loggedIn,
            creationSource: (authStore.siteSourceId ?? 2).toString(),
        };
    }

    trackAll(name: string, attrs?: Attrs) {
        setTimeout(() => {
            this.trackWebengage(name, attrs);
            this.trackPosthog(name, attrs);
            this.trackGtm(name, attrs);
            this.trackMetrix(name, attrs);
        });
    }

    sendSrpEvent(status: SrpEventStatus, trackers?: TrackerType[]) {
        const uniqueTrackers = Array.from(new Set(trackers ?? []));
        const searchFormStore = useSearchFormStore();
        let eventName: string;
        switch (searchFormStore.reserveType) {
            case ReserveType.Flight:
                eventName = 'srp_flight';
                break;
            case ReserveType.Train:
                eventName = 'srp_train';
                break;
            case ReserveType.Bus:
                eventName = 'srp_bus';
                break;
            case ReserveType.Taxi:
                eventName = 'srp_taxi';
                break;
            case ReserveType.Hotel:
                eventName = 'srp_hotel';
                break;
            default:
                eventName = 'srp';
                break;
        }
        const attrs = {
            status,
            ...this.searchResultsAttributes,
        };
        if (!uniqueTrackers.length) {
            this.trackAll('srp', attrs);
            this.trackAll(eventName, attrs);
        } else {
            setTimeout(() => {
                for (const tracker of uniqueTrackers) {
                    switch (tracker) {
                        case 'posthog':
                            this.trackPosthog('srp', attrs);
                            this.trackPosthog(eventName, attrs);
                            break;
                        case 'gtm':
                            this.trackGtm('srp', attrs);
                            this.trackGtm(eventName, attrs);
                            break;
                        case 'webengage':
                            this.trackWebengage('srp', attrs);
                            this.trackWebengage(eventName, attrs);
                            break;
                    }
                }
            });
        }
    }

    sendRouteSrpEvent(status: SrpEventStatus, params: any = {}) {
        const attrs = {
            status,
            ...this.searchResultsAttributes,
            ...params,
        };
        this.trackAll('srp_masir', attrs);
    }

    get searchResultsAttributes(): AnalyticsAttribute {
        const currentSearchForm = useCurrentSearchForm();
        const currentModule = useCurrentItineraryModule();
        const authStore = useAuthStore();
        return {
            ...currentSearchForm.value.searchAttributes,
            ...currentModule.value.searchResultAttributes,
            creationSource: (authStore.siteSourceId ?? 2).toString(),
            whiteLabel: useSiteConfigs().title,
        };
    }

    get tripEventMetric(): AnalyticsAttribute {
        const currentSearchForm = useCurrentSearchForm();
        const attrs = currentSearchForm.value.searchAttributes;
        const fromDate = attrs?.fromDate;
        const toDate = attrs?.toDate;
        const authStore = useAuthStore();
        const tripLength =
            (currentSearchForm.value as TripSearchForm)?.tripType ===
            TripType.RoundTrip
                ? dayConfig
                      .js(attrs.departureDate as string)
                      .diff(attrs.returnDate as string, 'day')
                : undefined;

        const passengerCount =
            currentSearchForm.value.adultCount +
            currentSearchForm.value.infantCount +
            currentSearchForm.value.childCount;
        return {
            ...attrs,
            checkInDate:
                attrs.reserveType === ReserveType.Hotel
                    ? fromDate || undefined
                    : undefined,
            checkoutDate:
                attrs.reserveType === ReserveType.Hotel
                    ? toDate || undefined
                    : undefined,
            isInterNational: currentSearchForm.value.isDomestic ? 0 : 1,
            tripType:
                (currentSearchForm.value as TripSearchForm)?.tripType || null,
            tripLength,
            passengerCount,
            exclusiveCoupe: (currentSearchForm.value as TrainSearchForm)
                ?.exclusive
                ? 1
                : 0,
            adultCount: currentSearchForm.value.adultCount,
            vpn: authStore.vpn,
            infantCount: currentSearchForm.value.infantCount,
            childCount: currentSearchForm.value.childCount,
        };
    }

    get searchResultsAttributesForReserve(): AnalyticsAttribute {
        return {
            ...this.searchResultsAttributes,
            creationSource: Number(this.searchResultsAttributes.creationSource),
        };
    }

    async sendUserLog(params: SearchLogAPIApiApiSearchLogAddlogPostRequest) {
        try {
            await SearchLogAPIApiApiSearchLogAddlogPost(params);
        } catch (e) {
            reportException(e);
        }
    }
}

export const { use: useAnalyticsStore } = defineClassStore(
    'analytics',
    () => new AnalyticsStore()
);
